/**
 * ============================================================
 *            Shopify Global Helpers (Extended)
 * ============================================================
 *
 * DealsCrafter V2 pricing helpers.
 *
 * Discount configuration is provided through the canonical
 * DealsCrafter V2 product configuration:
 *
 * - mode === "product"  → product
 * - mode === "variant" → variantDiscounts.items
 *
 * - preSaleMode === "product"  → productPreSale
 * - preSaleMode === "variant" → variantPreSaleDiscounts.items
 *
 * Variant discounts are resolved using the current Shopify
 * ProductVariant ID. When variant mode is active and no
 * discount is configured for the selected variant, no
 * product-level fallback is applied.
 *
 * Example DealsCrafter V2 configuration:
 *
 * {
 *   "version": 1,
 *   "mode": "variant",
 *   "preSaleMode": "product",
 *   "product": {
 *     "discount": "10",
 *     "discountType": "percentage"
 *   },
 *   "productPreSale": {
 *     "discount": "15",
 *     "discountType": "percentage"
 *   },
 *   "variantDiscounts": {
 *     "items": [
 *       {
 *         "variantId": "gid://shopify/ProductVariant/123456789",
 *         "discount": "20",
 *         "discountType": "percentage"
 *       }
 *     ]
 *   },
 *   "variantPreSaleDiscounts": {
 *     "items": []
 *   }
 * }
 *
 * The pricing helpers accept either a Shopify ProductVariant
 * GID or the numeric variant ID returned by input[name="id"].
 *
 * ============================================================
 */
// Utility functions (same as before)
function parsePrice(priceStr) {
  if (typeof priceStr === 'number') return priceStr;
  const parsed = parseFloat(String(priceStr).replace(/[^0-9.]/g, ''));
  return isNaN(parsed) ? 0 : parsed;
}

function formatPrice(num) {
  return `$${num.toFixed(2)}`;
}

function applyDiscount(price, discount, type) {
  const value = parseFloat(discount);
  if (isNaN(value)) return price;

  switch (type) {
    case 'percentage':
      return price - price * (value / 100);
    case 'amount':
      return Math.max(0, price - value);
    default:
      return price;
  }
}

/**
 * Calculates a discounted price using DealsCrafter V2 configuration.
 *
 * DealsCrafter V2 determines the discount source independently for
 * normal and pre-sale pricing:
 *
 * Normal:
 * - mode === "product" → product
 * - mode === "variant" → variantDiscounts.items
 *
 * Pre-Sale:
 * - preSaleMode === "product" → productPreSale
 * - preSaleMode === "variant" → variantPreSaleDiscounts.items
 *
 * When variant mode is active and the current variant has no configured
 * discount, no product-level fallback is applied.
 *
 * The function accepts either a Shopify ProductVariant GID or the
 * numeric variant ID commonly returned by input[name="id"].
 *
 * @param {string|number} variantId - Shopify ProductVariant ID or GID.
 * @param {string|number} priceInput - Base price with or without "$".
 * @param {Object} dealsCrafterConfig - DealsCrafter V2 product configuration.
 * @param {"normal"|"presale"} [type="normal"] - Discount context.
 * @returns {string|null} Formatted discounted price (e.g. "$479.40"),
 * or null if no discount applies.
 *
 * @example
 * const variantId = document.querySelector('input[name="id"]').value;
 *
 * const discountedPrice = getDiscountedPrice(
 *     variantId,
 *     "$799.00",
 *     dealsCrafterConfig,
 *     "normal"
 * );
 *
 * // "$639.20"
 *
 * @example
 * const discountedPrice = getDiscountedPrice(
 *     variantId,
 *     "$799.00",
 *     dealsCrafterConfig,
 *     "presale"
 * );
 */
window.getDiscountedPrice = function (
    variantId,
    priceInput,
    dealsCrafterConfig,
    type = "normal"
) {
    try {
        if (!dealsCrafterConfig) {
            console.warn(
                "[getDiscountedPrice] Missing DealsCrafter configuration."
            );
            return null;
        }

        if (!variantId) {
            console.warn(
                "[getDiscountedPrice] Missing variant ID."
            );
            return null;
        }

        const basePrice = parsePrice(priceInput);

        /*
         * Normalize the variant ID so the function accepts either:
         *
         * 123456789
         *
         * or:
         *
         * gid://shopify/ProductVariant/123456789
         */
        const normalizedVariantId = String(variantId).startsWith(
            "gid://shopify/ProductVariant/"
        )
            ? String(variantId)
            : `gid://shopify/ProductVariant/${variantId}`;

        let discountData = null;

        /*
         * Resolve the discount source based on the requested context.
         */
        if (type === "presale") {
            const mode = dealsCrafterConfig.preSaleMode;

            if (mode === "product") {
                discountData = dealsCrafterConfig.productPreSale;
            } else if (mode === "variant") {
                const variantDiscounts =
                    dealsCrafterConfig.variantPreSaleDiscounts?.items || [];

                discountData = variantDiscounts.find(
                    (item) => item.variantId === normalizedVariantId
                );
            } else {
                console.warn(
                    `[getDiscountedPrice] Invalid preSaleMode "${mode}".`
                );
                return null;
            }
        } else if (type === "normal") {
            const mode = dealsCrafterConfig.mode;

            if (mode === "product") {
                discountData = dealsCrafterConfig.product;
            } else if (mode === "variant") {
                const variantDiscounts =
                    dealsCrafterConfig.variantDiscounts?.items || [];

                discountData = variantDiscounts.find(
                    (item) => item.variantId === normalizedVariantId
                );
            } else {
                console.warn(
                    `[getDiscountedPrice] Invalid mode "${mode}".`
                );
                return null;
            }
        } else {
            console.warn(
                `[getDiscountedPrice] Unknown discount type "${type}".`
            );
            return null;
        }

        /*
         * No configured discount means the original price should
         * remain unchanged.
         *
         * In variant mode, this intentionally does NOT fall back
         * to the product-level discount.
         */
        if (
            !discountData ||
            discountData.discount === undefined ||
            discountData.discount === null ||
            discountData.discount === "" ||
            Number(discountData.discount) === 0
        ) {
            console.log(
                "[getDiscountedPrice] No discount found for:",
                {
                    variantId: normalizedVariantId,
                    type
                }
            );

            return null;
        }

        /*
         * Apply the configured discount.
         *
         * DealsCrafter V2 uses:
         * - percentage
         * - amount
         *
         * "fixed_amount" is also accepted for compatibility.
         */
        const discountType = discountData.discountType;

        let discountedPrice;

        if (discountType === "percentage") {
            discountedPrice = applyDiscount(
                basePrice,
                discountData.discount,
                "percentage"
            );
        } else if (
            discountType === "amount" ||
            discountType === "fixed_amount"
        ) {
            discountedPrice = applyDiscount(
                basePrice,
                discountData.discount,
                "amount"
            );
        } else {
            console.warn(
                `[getDiscountedPrice] Invalid discountType "${discountType}".`,
                discountData
            );
            return null;
        }

        return formatPrice(discountedPrice);
    } catch (error) {
        console.error(
            "[getDiscountedPrice] Error:",
            error
        );

        return null;
    }
};

/**
 * Returns the configured discount for a product variant using
 * DealsCrafter V2 configuration.
 *
 * DealsCrafter V2 determines the discount source independently
 * for normal and pre-sale pricing:
 *
 * Normal:
 * - mode === "product" → product
 * - mode === "variant" → variantDiscounts.items
 *
 * Pre-Sale:
 * - preSaleMode === "product" → productPreSale
 * - preSaleMode === "variant" → variantPreSaleDiscounts.items
 *
 * When variant mode is active and the current variant has no
 * configured discount, no product-level fallback is applied.
 *
 * The function accepts either a Shopify ProductVariant GID or
 * the numeric variant ID commonly returned by input[name="id"].
 *
 * @param {string|number} variantId - Shopify ProductVariant ID or GID.
 * @param {Object} dealsCrafterConfig - DealsCrafter V2 product configuration.
 * @param {"normal"|"presale"} [type="normal"] - Discount context.
 * @returns {{discount_type: string, discount_value: string}|null}
 * Discount type and value, or null if no discount applies.
 *
 * @example
 * const variantId = document.querySelector('input[name="id"]').value;
 *
 * const discount = getDiscount(
 *     variantId,
 *     dealsCrafterConfig,
 *     "normal"
 * );
 *
 * // {
 * //     discount_type: "percentage",
 * //     discount_value: "20"
 * // }
 *
 * @example
 * const discount = getDiscount(
 *     variantId,
 *     dealsCrafterConfig,
 *     "presale"
 * );
 */
window.getDiscount = function (
    variantId,
    dealsCrafterConfig,
    type = "normal"
) {
    try {
        if (!dealsCrafterConfig) {
            console.warn(
                "[getDiscount] Missing DealsCrafter configuration."
            );
            return null;
        }

        if (!variantId) {
            console.warn(
                "[getDiscount] Missing variant ID."
            );
            return null;
        }

        /*
         * Normalize the variant ID so the function accepts either:
         *
         * 123456789
         *
         * or:
         *
         * gid://shopify/ProductVariant/123456789
         */
        const normalizedVariantId = String(variantId).startsWith(
            "gid://shopify/ProductVariant/"
        )
            ? String(variantId)
            : `gid://shopify/ProductVariant/${variantId}`;

        let discountData = null;

        /*
         * Resolve the discount source based on the requested context.
         */
        if (type === "presale") {
            const mode = dealsCrafterConfig.preSaleMode;

            if (mode === "product") {
                discountData = dealsCrafterConfig.productPreSale;
            } else if (mode === "variant") {
                const variantDiscounts =
                    dealsCrafterConfig.variantPreSaleDiscounts?.items || [];

                discountData = variantDiscounts.find(
                    (item) => item.variantId === normalizedVariantId
                );
            } else {
                console.warn(
                    `[getDiscount] Invalid preSaleMode "${mode}".`
                );
                return null;
            }
        } else if (type === "normal") {
            const mode = dealsCrafterConfig.mode;

            if (mode === "product") {
                discountData = dealsCrafterConfig.product;
            } else if (mode === "variant") {
                const variantDiscounts =
                    dealsCrafterConfig.variantDiscounts?.items || [];

                discountData = variantDiscounts.find(
                    (item) => item.variantId === normalizedVariantId
                );
            } else {
                console.warn(
                    `[getDiscount] Invalid mode "${mode}".`
                );
                return null;
            }
        } else {
            console.warn(
                `[getDiscount] Unknown discount type "${type}".`
            );
            return null;
        }

        /*
         * No configured discount means no discount should be returned.
         *
         * In variant mode, this intentionally does NOT fall back
         * to the product-level discount.
         */
        if (
            !discountData ||
            discountData.discount === undefined ||
            discountData.discount === null ||
            discountData.discount === "" ||
            Number(discountData.discount) === 0
        ) {
            console.log(
                "[getDiscount] No discount found for:",
                {
                    variantId: normalizedVariantId,
                    type
                }
            );

            return null;
        }

        /*
         * Validate the configured discount type.
         */
        const discountType = discountData.discountType;

        if (
            discountType !== "percentage" &&
            discountType !== "amount" &&
            discountType !== "fixed_amount"
        ) {
            console.warn(
                `[getDiscount] Invalid discountType "${discountType}".`,
                discountData
            );
            return null;
        }

        return {
            discount_type: discountType,
            discount_value: discountData.discount
        };
    } catch (error) {
        console.error(
            "[getDiscount] Error:",
            error
        );

        return null;
    }
};

function formatDiscountAmount(discountType, discountValue) {
    if (discountType === "percentage") {
        return `${discountValue}%`;
    }

    if (
        discountType === "amount" ||
        discountType === "fixed_amount"
    ) {
        return `$${discountValue}`;
    }

    return null;
}

/**
 * Returns a discount badge as a real HTMLElement ready to append.
 *
 * DealsCrafter V2 determines the discount source independently for
 * normal and pre-sale pricing:
 *
 * Normal:
 * - mode === "product" → product
 * - mode === "variant" → variantDiscounts.items
 *
 * Pre-Sale:
 * - preSaleMode === "product" → productPreSale
 * - preSaleMode === "variant" → variantPreSaleDiscounts.items
 *
 * When variant mode is active and the current variant has no configured
 * discount, no product-level fallback is applied.
 *
 * The function accepts either a Shopify ProductVariant GID or the
 * numeric variant ID commonly returned by the product form input[name="id"].
 *
 * @param {string|number} variantId - Shopify ProductVariant ID or GID.
 * @param {Object} dealsCrafterConfig - DealsCrafter V2 product configuration.
 * @param {"normal"|"presale"} [type="normal"] - Discount context.
 * @returns {HTMLElement|null} A <span> element with the discount message,
 * or null if no discount applies.
 *
 * @example
 * const variantId = document.querySelector('input[name="id"]').value;
 *
 * const badge = getDiscountBadge(
 *     variantId,
 *     dealsCrafterConfig,
 *     "normal"
 * );
 *
 * if (badge) {
 *     container.appendChild(badge);
 * }
 *
 * @example
 * const badge = getDiscountBadge(
 *     "gid://shopify/ProductVariant/123456789",
 *     dealsCrafterConfig,
 *     "presale"
 * );
 */
window.getDiscountBadge = function (
    variantId,
    dealsCrafterConfig,
    type = "normal"
) {
    if (!dealsCrafterConfig) {
        console.warn(
            "[getDiscountBadge] Missing DealsCrafter configuration."
        );
        return null;
    }

    if (!variantId) {
        console.warn(
            "[getDiscountBadge] Missing variant ID."
        );
        return null;
    }

    /*
     * Normalize the variant ID so the function can accept either:
     *
     * 123456789
     *
     * or:
     *
     * gid://shopify/ProductVariant/123456789
     */
    const normalizedVariantId = String(variantId).startsWith(
        "gid://shopify/ProductVariant/"
    )
        ? String(variantId)
        : `gid://shopify/ProductVariant/${variantId}`;

    let discountData = null;

    /*
     * Resolve the discount source based on the requested context.
     */
    if (type === "presale") {
        const mode = dealsCrafterConfig.preSaleMode;

        if (mode === "product") {
            discountData = dealsCrafterConfig.productPreSale;
        } else if (mode === "variant") {
            const variantDiscounts =
                dealsCrafterConfig.variantPreSaleDiscounts?.items || [];

            discountData = variantDiscounts.find(
                (item) => item.variantId === normalizedVariantId
            );
        } else {
            console.warn(
                `[getDiscountBadge] Invalid preSaleMode "${mode}".`
            );
            return null;
        }
    } else if (type === "normal") {
        const mode = dealsCrafterConfig.mode;

        if (mode === "product") {
            discountData = dealsCrafterConfig.product;
        } else if (mode === "variant") {
            const variantDiscounts =
                dealsCrafterConfig.variantDiscounts?.items || [];

            discountData = variantDiscounts.find(
                (item) => item.variantId === normalizedVariantId
            );
        } else {
            console.warn(
                `[getDiscountBadge] Invalid mode "${mode}".`
            );
            return null;
        }
    } else {
        console.warn(
            `[getDiscountBadge] Unknown discount type "${type}".`
        );
        return null;
    }

    /*
     * No configured discount means no badge.
     *
     * In variant mode, this intentionally does NOT fall back
     * to the product-level discount.
     */
    if (
        !discountData ||
        discountData.discount === undefined ||
        discountData.discount === null ||
        discountData.discount === "" ||
        Number(discountData.discount) === 0
    ) {
        console.log(
            "[getDiscountBadge] No discount found for:",
            {
                variantId: normalizedVariantId,
                type
            }
        );

        return null;
    }

    /*
     * Format the discount according to the configured discount type.
     *
     * Percentage:
     *   20 → "20%"
     *
     * Fixed amount:
     *   20 → "$20"
     *
     * Both "amount" and "fixed_amount" are supported for
     * compatibility with DealsCrafter discount configuration.
     */
    const discountType = discountData.discountType;
    const discountAmount = formatDiscountAmount(
        discountType,
        discountData.discount
    );

    if (!discountAmount) {
        console.warn(
            `[getDiscountBadge] Invalid discountType "${discountType}".`,
            discountData
        );
        return null;
    }

    /*
     * Create the badge element.
     */
    const span = document.createElement("span");

    span.className = "discount-msg";

    span.textContent =
        `${discountAmount} OFF - FALL SALE PRICE APPLIED AT CHECKOUT`;

    span.dataset.variant = normalizedVariantId;
    span.dataset.type = type;

    console.log(
        "[getDiscountBadge] Created badge:",
        span
    );

    return span;
};

/**
 * Injects or updates a DealsCrafter V2 discount badge inside
 * elements with the class "shopify-discount-badge-dc".
 *
 * The discount is resolved by getDiscountBadge() using the
 * provided DealsCrafter V2 configuration.
 *
 * @param {string|number} variantId - Shopify ProductVariant ID or GID.
 * @param {Object} dealsCrafterConfig - DealsCrafter V2 product configuration.
 * @param {"normal"|"presale"} [type="normal"] - Discount context.
 * @returns {void}
 *
 * @example
 * const variantId = document.querySelector('input[name="id"]').value;
 *
 * updateDiscountBadge(
 *     variantId,
 *     dealsCrafterConfig,
 *     "normal"
 * );
 *
 * @example
 * updateDiscountBadge(
 *     variantId,
 *     dealsCrafterConfig,
 *     "presale"
 * );
 */
window.updateDiscountBadge = function (
    variantId,
    dealsCrafterConfig,
    type = "normal"
) {
    try {
        console.log(
            "[updateDiscountBadge]",
            {
                variantId,
                type
            }
        );

        const priceElements = document.getElementsByClassName(
            "shopify-discount-badge-dc"
        );

        if (!priceElements.length) {
            console.warn(
                '[updateDiscountBadge] No elements found with class "shopify-discount-badge-dc".'
            );
            return;
        }

        const discountMessage = getDiscountBadge(
            variantId,
            dealsCrafterConfig,
            type
        );

        /*
         * Remove any existing badge first.
         *
         * This is important when switching between variants,
         * because the previous variant may have had a discount
         * while the new variant does not.
         */
        Array.from(priceElements).forEach((priceElement) => {
            const existingBadges =
                priceElement.querySelectorAll(".discount-msg");

            existingBadges.forEach((badge) => badge.remove());
        });

        /*
         * No applicable discount means the badge should remain
         * removed. This is especially important for variant mode.
         */
        if (!discountMessage) {
            console.log(
                "[updateDiscountBadge] No applicable discount for:",
                {
                    variantId,
                    type
                }
            );
            return;
        }

        /*
         * getDiscountBadge() returns a real HTMLElement.
         * Clone it so each target element receives its own node.
         */
        if (!(discountMessage instanceof HTMLElement)) {
            console.warn(
                "[updateDiscountBadge] Unexpected type returned from getDiscountBadge():",
                discountMessage
            );
            return;
        }

        Array.from(priceElements).forEach((priceElement) => {
            const badgeElement = discountMessage.cloneNode(true);

            priceElement.appendChild(badgeElement);

            console.log(
                "[updateDiscountBadge] Discount badge added:",
                {
                    variantId,
                    type
                }
            );
        });
    } catch (error) {
        console.error(
            "[updateDiscountBadge] Error while updating badge:",
            error
        );
    }
};

window.formatMoney = function (amount, locale = "en-US", currency = "USD") {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
};

/**
 * Calculates and updates the bundle total price displayed on the page.
 * Automatically handles normal vs presale discounts.
 *
 * @param {Array} bundleProducts - Array of products in the bundle.
 * @param {Array} discounts - Array of discount objects matching bundleProducts.
 * @param {HTMLElement} totalPriceElement - The DOM element where the total must be displayed.
 *
 * @example
 * updateBundleTotal(bundleProducts, discounts, document.getElementById("bundle-total"));
 */
window.updateBundleTotal = function (bundleProducts, discounts, totalPriceElement,normalPrice) {
    try {

        // --- Safety checks ---
        if (!bundleProducts || !Array.isArray(bundleProducts)) {
            console.warn("[updateBundleTotal] Invalid or missing bundleProducts array.");
            return;
        }
        if (!discounts || !Array.isArray(discounts)) {
            console.warn("[updateBundleTotal] Invalid or missing discounts array.");
            return;
        }
        if (!totalPriceElement) {
            console.warn("[updateBundleTotal] Missing totalPriceElement (DOM target).");
            return;
        }
        if (typeof window.getSelectedVariantPrice !== "function") {
            console.warn("[updateBundleTotal] Missing required function: getSelectedVariantPrice()");
            return;
        }
        if (typeof window.formatMoney !== "function") {
            console.warn("[updateBundleTotal] Missing required function: formatMoney()");
            return;
        }

        // --- Check if presale is enabled for at least one product ---
        const hasPresale = global_configs.isPreSaleEnabled;

        let subtotal = 0;
        let totalWithAmount = 0;
        let totalWithPreSale = 0;

        // --- Process each product in the bundle ---
        bundleProducts.forEach((_, i) => {
            const basePrice = parseFloat(window.getSelectedVariantPrice(bundleProducts,i)) || 0;

            subtotal += basePrice;

            const discountData = discounts[i] || {};
            const amount = parseFloat(discountData.amount) || 0;
            const preSaleAmount = parseFloat(discountData.preSaleAmount) || 0;
            const type = discountData.type || "percentage";

            if (type === "percentage") {
                const normalMultiplier = 1 - amount / 100;
                const preSaleMultiplier = 1 - preSaleAmount / 100;

                totalWithAmount += basePrice * normalMultiplier;
                totalWithPreSale += basePrice * preSaleMultiplier;
            }
            else if (type === "fixed") {
                totalWithAmount += Math.max(0, basePrice - amount);
                totalWithPreSale += Math.max(0, basePrice - preSaleAmount);
            }
            else {
                totalWithAmount += basePrice;
                totalWithPreSale += basePrice;
            }
        });

        // --- Format totals ---
        const formattedPrice  = window.formatMoney(subtotal);
        const formattedAmount = window.formatMoney(totalWithAmount);
        const formattedPreSale = window.formatMoney(totalWithPreSale);

        // --- Update DOM ---
        normalPrice.innerHTML = formattedPrice;

        totalPriceElement.innerHTML = hasPresale
        ? formattedPreSale
        : formattedAmount;

    } catch (error) {
        console.error("[updateBundleTotal] Error:", error);
    }
};

/**
 * Returns the selected variant price for a bundle product.
 * Matches variant options based on all <select> dropdowns inside the product card.
 *
 * @global
 * @param {number} bundleProductIndex - Index of the bundle product.
 * @returns {number} Variant price in standard currency units (e.g. 199.99)
 */
window.getSelectedVariantPrice = function (bundleProducts,bundleProductIndex) {
    try {
        const productCards = document.querySelectorAll(".bundle-product__card-item");

        if (!productCards.length) {
            console.warn("[getSelectedVariantPrice] No .bundle-product__card-item elements found.");
            return 0;
        }

        const product = bundleProducts[bundleProductIndex];

        if (!product || !product.variants) {
            console.warn(`[getSelectedVariantPrice] Product at index ${bundleProductIndex} has no variants.`);
            return 0;
        }

        // --- Get all <select> dropdowns inside card ---
        const selects = productCards[bundleProductIndex].querySelectorAll("select");
        const selectedOptions = Array.from(selects).map((s) => s.value);

        // --- Match variant by comparing options array exactly ---
        const matchedVariant = product.variants.find(
        (variant) =>
            JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
        );

        if (!matchedVariant) {
        console.warn(
            `[getSelectedVariantPrice] No matching variant found at index ${bundleProductIndex}. Selected options:`,
            selectedOptions
        );
        return 0;
        }

        // Shopify variant.price is in cents → convert to dollars
        return matchedVariant.price / 100;

    } catch (error) {
        console.error("[getSelectedVariantPrice] Error:", error);
        return 0;
    }
};

window.updateCardPrices = function () {
    const priceSpans = document.querySelectorAll("span.product-price--displayed.js-product-price");

    priceSpans.forEach(span => {
        const parentElement         = span.parentElement;                // Padre del span
        const originalPrice         = span.textContent.trim();                 // Contenido del span
        const productModel          = span.getAttribute("data-product-model");
        const productType           = span.getAttribute("data-product-type");
        const dealsCrafterProduct   = span.getAttribute("data-deals-crafter-product");

        if(dealsCrafterProduct != 'true') {
            return;
        }

        if(productType == 'Air Purifier') {
            const variantId = span.getAttribute("data-variant-id");
            const dealsCrafterConfigValue = span.getAttribute(
                "data-deals-crafter-config"
            );

            if (!variantId || !dealsCrafterConfigValue) {
                return;
            }

            let dealsCrafterConfig;

            try {
                dealsCrafterConfig = JSON.parse(dealsCrafterConfigValue);
            } catch (error) {
                return;
            }

            const isPreSaleProduct =
                document.getElementById("shopify-is-presale-product")?.value;
            const isPreSaleContext =
                global_configs &&
                global_configs.isPreSaleEnabled &&
                (!isPreSaleProduct ||
                    String(isPreSaleProduct).toLowerCase() === "true");
            const discountedPrice = getDiscountedPrice(
                variantId,
                originalPrice,
                dealsCrafterConfig,
                isPreSaleContext ? "presale" : "normal"
            );

            if (discountedPrice === null) {
                return;
            }

            span.textContent = discountedPrice;
            span.classList.add("discounted-price-show");

            const newSpan = document.createElement("span");
            newSpan.className = "inline-block mls";

            // Crear <strike>
            const strike = document.createElement("strike");
            strike.id = `card-compare-at-price-${productModel}`;
            strike.setAttribute("data-class", "is-showing");
            strike.className = "product-price--compared js-product-compare-price compare-price--grid";
            strike.textContent = originalPrice;
            newSpan.appendChild(strike);
            parentElement.appendChild(newSpan);
        } else {

            const bundleVariants = JSON.parse(span.getAttribute("data-bundle-variants"));
            const bundleConfig   = JSON.parse(span.getAttribute("data-bundle-config"));


            // Convertimos descuentos a un arreglo con los mismos índices de productos
            const discounts = bundleConfig ? bundleConfig.items.map(item => ({
                amount: item.amount,
                preSaleAmount: item.preSaleAmount,
                type: item.type
            })) : [];

            const bundleProducts = bundleVariants.map((variant, index) => ({
                index,
                variants: variant
            }));


            const compareStrike = span.nextElementSibling?.querySelector("strike");

            updateBundleCard(bundleProducts,discounts,span,compareStrike);


        }

    });
}

window.updateBundleCard = function (bundleProducts, discounts, totalPriceElement,normalPrice) {
    try {
        // --- Safety checks ---
        if (!bundleProducts || !Array.isArray(bundleProducts)) {
            console.warn("[updateBundleTotal] Invalid or missing bundleProducts array.");
            return;
        }
        if (!discounts || !Array.isArray(discounts)) {
            console.warn("[updateBundleTotal] Invalid or missing discounts array.");
            return;
        }
        if (!totalPriceElement) {
            console.warn("[updateBundleTotal] Missing totalPriceElement (DOM target).");
            return;
        }
        if (typeof window.getSelectedVariantPrice !== "function") {
            console.warn("[updateBundleTotal] Missing required function: getSelectedVariantPrice()");
            return;
        }
        if (typeof window.formatMoney !== "function") {
            console.warn("[updateBundleTotal] Missing required function: formatMoney()");
            return;
        }

        // --- Check if presale is enabled for at least one product ---
        const hasPresale = global_configs.isPreSaleEnabled;

        let subtotal = 0;
        let totalWithAmount = 0;
        let totalWithPreSale = 0;

        // --- Process each product in the bundle ---
        bundleProducts.forEach((_, i) => {

            const numericString = normalPrice.textContent.trim().replace(/[$,]/g, "");
            const priceNumber = parseFloat(numericString);

            const basePrice = priceNumber;
            subtotal += basePrice;

            const discountData = discounts[i] || {};
            const amount = parseFloat(discountData.amount) || 0;
            const preSaleAmount = parseFloat(discountData.preSaleAmount) || 0;
            const type = discountData.type || "percentage";

            if (type === "percentage") {
                const normalMultiplier = 1 - amount / 100;
                const preSaleMultiplier = 1 - preSaleAmount / 100;

                totalWithAmount += basePrice * normalMultiplier;
                totalWithPreSale += basePrice * preSaleMultiplier;
            }
            else if (type === "fixed") {
                totalWithAmount += Math.max(0, basePrice - amount);
                totalWithPreSale += Math.max(0, basePrice - preSaleAmount);
            }
            else {
                totalWithAmount += basePrice;
                totalWithPreSale += basePrice;
            }
        });

        // --- Format totals ---
        const formattedPrice  = window.formatMoney(subtotal);
        const formattedAmount = window.formatMoney(totalWithAmount);
        const formattedPreSale = window.formatMoney(totalWithPreSale);

        // --- Update DOM ---
        normalPrice.innerHTML = formattedPrice;

        totalPriceElement.innerHTML = hasPresale
        ? formattedPreSale
        : formattedAmount;

    } catch (error) {
        console.error("[updateBundleTotal] Error:", error);
    }
};

function normalizePromoBadgeDiscount(discount) {
    if (!discount) {
        return null;
    }

    const discountType = discount.discountType || discount.discount_type;
    const discountValue = Number(
        discount.discount ?? discount.discount_value
    );

    if (
        !formatDiscountAmount(discountType, discountValue) ||
        !Number.isFinite(discountValue) ||
        discountValue <= 0
    ) {
        return null;
    }

    return {
        discount_type: discountType,
        discount_value: discountValue
    };
}

window.getDealsCrafterPromoBadgeDiscount = function (
    dealsCrafterConfig,
    type = "normal",
    variantId = null
) {
    if (!dealsCrafterConfig) {
        return null;
    }

    const mode =
        type === "presale"
            ? dealsCrafterConfig.preSaleMode
            : type === "normal"
                ? dealsCrafterConfig.mode
                : null;

    if (mode === "product") {
        return normalizePromoBadgeDiscount(
            type === "presale"
                ? dealsCrafterConfig.productPreSale
                : dealsCrafterConfig.product
        );
    }

    if (mode !== "variant") {
        return null;
    }

    const variantDiscounts =
        type === "presale"
            ? dealsCrafterConfig.variantPreSaleDiscounts?.items || []
            : dealsCrafterConfig.variantDiscounts?.items || [];

    if (variantId) {
        const normalizedVariantId = String(variantId).startsWith(
            "gid://shopify/ProductVariant/"
        )
            ? String(variantId)
            : `gid://shopify/ProductVariant/${variantId}`;
        const selectedDiscount = variantDiscounts.find(
            (discount) =>
                String(discount?.variantId) === normalizedVariantId
        );

        return normalizePromoBadgeDiscount(selectedDiscount);
    }

    return variantDiscounts.reduce(
        (largest, discount) => {
            const normalizedDiscount = normalizePromoBadgeDiscount(discount);

            if (!normalizedDiscount) {
                return largest;
            }

            return !largest ||
                normalizedDiscount.discount_value > largest.discount_value
                ? normalizedDiscount
                : largest;
        },
        null
    );
};

window.createDealsCrafterPromoBadge = function (
    discount,
    options = {}
) {
    const normalizedDiscount = normalizePromoBadgeDiscount(discount);

    if (!normalizedDiscount) {
        return null;
    }

    const discountAmount = formatDiscountAmount(
        normalizedDiscount.discount_type,
        normalizedDiscount.discount_value
    );
    const classNames = [
        "badge-circle",
        options.positionClass,
        options.badgeClass
    ].filter(Boolean);

    const badge = document.createElement("div");
    badge.className = classNames.join(" ");

    const internalCircle = document.createElement("div");
    internalCircle.className = "internal-circle";

    const contentContainer = document.createElement("div");
    contentContainer.className = "content-container";

    const badgeContent = document.createElement("p");
    badgeContent.className = "dc-badge-text";

    if (options.prefix) {
        const prefixElement = document.createElement("span");
        prefixElement.className = "dc-badge-prefix";
        prefixElement.textContent = options.prefix;
        badgeContent.appendChild(prefixElement);
    }

    const discountElement = document.createElement("span");
    discountElement.className = "dc-badge-discount";

    const valueElement = document.createElement("span");
    valueElement.className = "dc-badge-value";
    valueElement.textContent = discountAmount;

    const offElement = document.createElement("span");
    offElement.className = "dc-badge-off";
    offElement.textContent = "OFF";

    discountElement.appendChild(valueElement);
    discountElement.appendChild(offElement);
    badgeContent.appendChild(discountElement);

    contentContainer.appendChild(badgeContent);
    internalCircle.appendChild(contentContainer);
    badge.appendChild(internalCircle);

    return badge;
};

window.getDealsCrafterPromoBadge = function (
    dealsCrafterConfig,
    options = {}
) {
    const type = options.type || "normal";
    const discount = window.getDealsCrafterPromoBadgeDiscount(
        dealsCrafterConfig,
        type,
        options.variantId || null
    );
    const mode =
        type === "presale"
            ? dealsCrafterConfig?.preSaleMode
            : dealsCrafterConfig?.mode;

    return window.createDealsCrafterPromoBadge(
        discount,
        {
            ...options,
            prefix:
                mode === "variant" && !options.variantId
                    ? "UP TO"
                    : ""
        }
    );
};

window.addPdpFilterPromoBadges = function () {
    const configElement = document.getElementById(
        "shopify-deals-crafter-config"
    );
    const isPreSaleProduct = document.getElementById(
        "shopify-is-presale-product"
    )?.value;
    const type =
        global_configs?.isPreSaleEnabled &&
        String(isPreSaleProduct).toLowerCase() === "true"
            ? "presale"
            : "normal";
    let dealsCrafterConfig;

    try {
        dealsCrafterConfig = configElement?.value
            ? JSON.parse(configElement.value)
            : null;
    } catch (error) {
        dealsCrafterConfig = null;
    }

    document.querySelectorAll(
        ".pdp-variant-filter-selector"
    ).forEach((filterElement) => {
        const existingBadge = filterElement.querySelector(
            ".pdp-filter-dealscrafter-badge"
        );

        if (existingBadge) {
            existingBadge.remove();
        }

        const discount = window.getDealsCrafterPromoBadgeDiscount(
            dealsCrafterConfig,
            type,
            filterElement.getAttribute("data-product-selected")
        );
        const discountAmount = discount
            ? formatDiscountAmount(
                discount.discount_type,
                discount.discount_value
            )
            : null;

        if (!discountAmount) {
            return;
        }

        const badge = document.createElement("span");
        badge.className = "pdp-filter-dealscrafter-badge";
        badge.textContent = `${discountAmount} OFF`;

        const header = filterElement.querySelector(
            ".pdp-variant-first-element"
        );
        const headerPrice = header?.querySelector(
            ".pdp-variant-header-price"
        );

        if (header && headerPrice) {
            header.insertBefore(badge, headerPrice);
        } else if (header) {
            header.appendChild(badge);
        }
    });
};

window.addPromoBadgesToMeganav = function () {

    const menuProductCards = document.querySelectorAll(".menu-product-card");

    menuProductCards.forEach(productCard => {
        const parentElement = productCard.parentElement;
        const productType = parentElement.getAttribute("data-product-type");

        if ('Air Purifier' == productType) {
            const variantId =
                productCard.getAttribute("data-variant-id") ||
                parentElement.getAttribute("data-variant-id");
            const configValue =
                productCard.getAttribute("data-deals-crafter-config") ||
                parentElement.getAttribute("data-deals-crafter-config");

            if (!variantId || !configValue) {
                return;
            }

            let dealsCrafterConfig;

            try {
                dealsCrafterConfig = JSON.parse(configValue);
            } catch (error) {
                return;
            }

            const isPreSaleProduct =
                parentElement.getAttribute("data-is-presale-product") ||
                document.getElementById("shopify-is-presale-product")?.value;
            const isPreSaleContext =
                global_configs &&
                global_configs.isPreSaleEnabled &&
                (!isPreSaleProduct ||
                    String(isPreSaleProduct).toLowerCase() === "true");
            const badge = window.getDealsCrafterPromoBadge(
                dealsCrafterConfig,
                {
                    type: isPreSaleContext ? "presale" : "normal",
                    positionClass: "meganav-position",
                    badgeClass: "dc-promo-badge"
                }
            );

            const currentBadge = productCard.querySelector(".dc-promo-badge");

            if (currentBadge) {
                currentBadge.remove();
            }

            if (badge) {
                productCard.appendChild(badge);
            }
        }
    });
}

window.addPromoBadgesComparisonGrid = function () {
    const comparisonProductCells =
        document.querySelectorAll(".t-cell.h-300");

    comparisonProductCells.forEach((productCell) => {
        const productType = productCell.getAttribute(
            "data-product-type"
        );

        if (productType !== "Air Purifier") {
            return;
        }

        const configValue = productCell.getAttribute(
            "data-deals-crafter-config"
        );
        const isPreSaleProduct = productCell.getAttribute(
            "data-is-presale-product"
        );
        const isPreSaleContext =
            global_configs?.isPreSaleEnabled &&
            String(isPreSaleProduct).toLowerCase() === "true";
        let dealsCrafterConfig;

        try {
            dealsCrafterConfig = configValue
                ? JSON.parse(configValue)
                : null;
        } catch (error) {
            dealsCrafterConfig = null;
        }

        const badge = window.getDealsCrafterPromoBadge(
            dealsCrafterConfig,
            {
                type: isPreSaleContext ? "presale" : "normal",
                positionClass: "comparison-grid-position",
                badgeClass: "dc-comparison-promo-badge"
            }
        );
        const existingBadge = productCell.querySelector(
            ".dc-comparison-promo-badge"
        );

        if (existingBadge) {
            existingBadge.remove();
        }

        if (badge) {
            productCell.appendChild(badge);
        }
    });
};

window.addPromoBadgesToPDPGallery = function () {

    const pdpGalleryContainer = document.querySelector(".grid__item.product__media-wrapper");

	if (pdpGalleryContainer) {

		const productType = pdpGalleryContainer.getAttribute("data-product-type");
		const variantIdElement = document.querySelector('input[name="id"]');
		const dealsCrafterConfigElement = document.getElementById("shopify-deals-crafter-config");
		const isPreSaleProductElement = document.getElementById("shopify-is-presale-product");

		if ('Air Purifier' == productType && variantIdElement && dealsCrafterConfigElement && isPreSaleProductElement) {
			let dealsCrafterConfig;

			try {
				dealsCrafterConfig = JSON.parse(dealsCrafterConfigElement.value || "{}");
			} catch (error) {
				return;
			}

			const isPreSaleContext =
				global_configs?.isPreSaleEnabled &&
				String(isPreSaleProductElement.value).toLowerCase() === "true";
			const badgeContainer = pdpGalleryContainer.querySelector('.slider-mobile-gutter');

			if (badgeContainer) {
				const currentBadge = badgeContainer.querySelector(".pdp-discount-badge");

				if (currentBadge) {
					currentBadge.remove();
				}

				const badge = window.getDealsCrafterPromoBadge(
					dealsCrafterConfig,
					{
						type: isPreSaleContext ? "presale" : "normal",
						variantId: variantIdElement.value,
						positionClass: "pdp-gallery-position",
						badgeClass: "pdp-discount-badge"
					}
				);

				if (badge) {
					badgeContainer.appendChild(badge);
				}
			}
		}
	}
}

window.addPresaleBadgeToProductCards = function () {

    const productsCards = document.querySelectorAll(".product-card-wrapper");

	if (productsCards) {
		productsCards.forEach(productCard => {

			const originalPrice         = productCard.getAttribute("data-product-price");
            const productTitle          = productCard.getAttribute("data-product-title");
			const productModel          = productCard.getAttribute("data-product-model");
			const productType           = productCard.getAttribute("data-product-type");

			if ('Air Purifier' == productType ) {
				const variantId =
					productCard.getAttribute("data-variant-id") ||
					productCard.closest("[data-variant-id]")?.getAttribute("data-variant-id");
				const dealsCrafterConfigElement =
					document.getElementById("shopify-deals-crafter-config");

				if (!variantId || !dealsCrafterConfigElement?.value) {
					return;
				}

				let dealsCrafterConfig;

				try {
					dealsCrafterConfig = JSON.parse(
						dealsCrafterConfigElement.value
					);
				} catch (error) {
					return;
				}

				const isPreSaleProduct =
					document.getElementById("shopify-is-presale-product")?.value;
				const isPreSaleContext =
					global_configs &&
					global_configs.isPreSaleEnabled &&
					(!isPreSaleProduct ||
						String(isPreSaleProduct).toLowerCase() === "true");

				if (!isPreSaleContext) {
					return;
				}

				const discount = getDiscount(
					variantId,
					dealsCrafterConfig,
					"presale"
				);

				if (!discount || discount.discount_value == 0) {
					return;
				}

				const badgeContainer = productCard.querySelector('.card__badge');

				if (badgeContainer) {

					const currentBadge = badgeContainer.querySelector(".badge");

					if (currentBadge) {
						currentBadge.remove();
					}

					const badge = document.createElement("span");
					badge.className = "badge badge--bottom-left color-inverse pre-sale-badge";

					if ("percentage" === discount.discount_type) {
						badge.innerHTML = 'Sale <span class="save-total">Save ' + discount.discount_value + '%</span>';
					} else {
						badge.innerHTML = 'Sale <span class="save-total">Save $' + discount.discount_value + '</span>';
					}

					badgeContainer.appendChild(badge);
				}

			} else if ('Bundle' == productType ) {
                if(global_configs && global_configs.isPreSaleEnabled) {
					if ( productTitle.includes('Home Smart') ) {
						const badgeContainer = productCard.querySelector('.card__badge');

						if (badgeContainer) {

						const currentBadge = badgeContainer.querySelector(".badge");

							if (currentBadge) {
							currentBadge.remove();
							}

							const badge = document.createElement("span");
							badge.className = "badge badge--bottom-left color-inverse pre-sale-badge";
							badge.innerHTML =  global_configs._pre_sale_label;
							badgeContainer.appendChild(badge);

						}
					}
				}
			}
		});
	}
}

window.addPromoBadgesToCollectionTabs = function () {
    const collectionCards = document.querySelectorAll(
        ".collection-tabs-product-card"
    );

    collectionCards.forEach((card) => {
        const imageContainer = card.querySelector(".card__media");
        const existingBadge = imageContainer?.querySelector(
            ".dc-collection-tabs-promo-badge"
        );

        if (existingBadge) {
            existingBadge.remove();
        }

        if (!imageContainer) {
            return;
        }

        const configValue = card.getAttribute(
            "data-deals-crafter-config"
        );
        let dealsCrafterConfig;

        try {
            dealsCrafterConfig = configValue
                ? JSON.parse(configValue)
                : null;
        } catch (error) {
            dealsCrafterConfig = null;
        }

        const isPreSaleProduct = card.getAttribute(
            "data-is-presale-product"
        );
        const isPreSaleContext =
            global_configs?.isPreSaleEnabled &&
            String(isPreSaleProduct).toLowerCase() === "true";
        const badge = window.getDealsCrafterPromoBadge(
            dealsCrafterConfig,
            {
                type: isPreSaleContext ? "presale" : "normal",
                positionClass: "collection-tabs-position",
                badgeClass: "dc-collection-tabs-promo-badge"
            }
        );

        if (badge) {
            imageContainer.appendChild(badge);
        }
    });
};

(function initializeCollectionTabsPromoBadges() {
    const initialize = () => {
        if (
            window.__collectionTabsPromoBadgesInitialized ||
            !document.querySelector(".collection-tabs-product-card")
        ) {
            return;
        }

        window.__collectionTabsPromoBadgesInitialized = true;
        window.addPromoBadgesToCollectionTabs();
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initialize, {
            once: true
        });
    } else {
        initialize();
    }
})();

// Debug info
console.info('>>> IMPROVING: [Helpers.js] getDiscountedPrice() loaded');
